import React from 'react'
import { Typography, Stack, Box, Button } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { QuestionFrame } from 'components/questions'
import { blue, lightBlue } from '@mui/material/colors'
import { useGlobalState } from 'state'
import axios from 'axios'
import { HashMap, YesNoValue } from 'common/types'
import { isNumeric, getQuestionKey, isDevelopment } from 'common/utils'
import * as api from 'common/api'


const BlueColumn = ({width, text, step, center = false}: {width: number, text: string, step: 50|100|300|500|700, center?: boolean}) => {
  return (
    <Box
      width={width}
      sx={{backgroundColor: lightBlue[step]}}
      display="flex"
      flexDirection="column"
      justifyContent='cener'
      alignItems={center ? 'center' : 'start'}
      py={1}
      px={2}
    >
      <Typography
        variant="body2"
        color={step >= 200 ? 'white' : 'black'}
        whiteSpace={'pre-wrap'}
        height={'100%'}
        alignContent={'center'}
        textAlign={center ? 'center' : 'start'}
      >
        {text}
      </Typography>
    </Box>
  )
}

type RiskAnswer = {
  risk: YesNoValue
  depth: string
  tp: string
  tpValue: string
}


export const Q01_04 = () => {
  const state = useGlobalState()
  const navigate = useNavigate()
  const { debugRiskCode } = useParams()

  const [calcResult, setCalcResult] = React.useState<string>('')

  const q1: HashMap = React.useMemo(() => {
    const answer = state.app.getAnswer(1, 1)
    return answer !== null ? answer.value : {risk: '', tp: ''}
  }, [state.app.answers])

  const q2: HashMap = React.useMemo(() => {
    const answer = state.app.getAnswer(2, 1)
    return answer !== null ? answer.value : {risk: '', tp: ''}
  }, [state.app.answers])

  const q3: HashMap = React.useMemo(() => {
    const answer = state.app.getAnswer(3, 1)
    return answer !== null ? answer.value : {risk: ''}
  }, [state.app.answers])

  const q4: HashMap = React.useMemo(() => {
    const answer = state.app.getAnswer(4, 1)
    return answer !== null ? answer.value : {risk: '', tp: ''}
  }, [state.app.answers])


  React.useEffect(() => {
    calcRisk()
  }, [])

  const calcTp = (depth: number, elevation: number): string => {
    if (depth === null || elevation === null) {
      return ''
    }
    const tp = Math.floor(depth * 100 + elevation * 100) / 100
    return tp.toFixed(1)
  }

  const getDepthRange = (depth: number | null): string => {
    if (depth === null) {
      return '-'
    }
    if (depth >= 20) {
      return '20.0〜'
    } else if (depth >= 10) {
      return '10.0〜20.0'
    } else if (depth >= 5) {
      return '5.0〜10.0'
    } else if (depth >= 3) {
      return '3.0〜5.0'
    } else if (depth >= 0.5) {
      return '0.5〜3.0'
    } else if (depth >= 0.1) {
      return '0.1〜0.5'
    } else if (depth > 0) {
      return '〜0.5'
    } else {
      return '-'
    }
  }

  const getTpRange = (depth: number | null, elevation: number | null): string => {
    if (depth === null || elevation === null) {
      return '-'
    }
    if (depth >= 20) {
      return calcTp(20, elevation) + '〜'
    } else if (depth >= 10) {
      return calcTp(10, elevation) + '〜' + calcTp(20, elevation)
    } else if (depth >= 5) {
      return calcTp(5, elevation) + '〜' + calcTp(10, elevation)
    } else if (depth >= 3) {
      return calcTp(3, elevation) + '〜' + calcTp(5, elevation)
    } else if (depth >= 0.5) {
      return calcTp(0.5, elevation) + '〜' + calcTp(3, elevation)
    } else if (depth >= 0.1) {
      return calcTp(0.1, elevation) + '〜' + calcTp(0.5, elevation)
    } else if (depth > 0) {
      return '〜' + calcTp(0.1, elevation)
    } else {
      return '-'
    }
  }

  const getCeilDepth = (depth: number | null): number | null => {
    if (depth === null || isNumeric(depth) === false) {
      return null
    }
    const d = Number(depth)
    if (d <= 0) {
      return null
    } else if (d <= 0.5) {
      return 0.5
    } else {
      // 1刻み
      return  Math.ceil(d)
    }
  }

  const calcRisk = async () => {
    // debug
    if (isDevelopment === true && debugRiskCode !== undefined) {
      let saidai = ''
      let keikaku = ''
      let naisui = ''
      if (debugRiskCode.includes('1')) {
        saidai = '3'
      }
      if (debugRiskCode.includes('2')) {
        keikaku = '5'
      }
      if (debugRiskCode.includes('4')) {
        naisui = '7'
      }
      const nextAnswers = {
        ...state.app.answers,
        map: {value: {lat: 0, lng: 0}, valid: true},
        [getQuestionKey(1, 1)]: {value: {risk: saidai !== '' ? 'はい' : 'いいえ', depth: '', tp: '', tpValue: saidai}, valid: true},
        [getQuestionKey(2, 1)]: {value: {risk: keikaku !== '' ? 'はい' : 'いいえ', depth: '', tp: '', tpValue: keikaku}, valid: true},
        [getQuestionKey(3, 1)]: {value: {risk: 'いいえ'}, valid: true},
        [getQuestionKey(4, 1)]: {value: {risk: naisui !== '' ? 'はい' : 'いいえ', depth: '', tp: '', tpValue: naisui}, valid: true},
      }
      state.app.setAnswers(nextAnswers)
      setCalcResult('success')
      return
    }

    const answer = state.app.getAnswerByKey('map')
    if (answer === null || !answer.value || !answer.value.lng || !answer.value.lat) {
      setCalcResult('none')
      return
    }
    if (answer.valid == true && answer.value.calcResult === 'success') {
      setCalcResult('success')
      return
    }

    state.app.setLoading(true)

    const q1Value: RiskAnswer = {risk: 'いいえ', depth: '', tp: '', tpValue: ''}
    const q2Value: RiskAnswer = {risk: 'いいえ', depth: '', tp: '', tpValue: ''}
    const q3Value: {risk: YesNoValue} = {risk: 'いいえ'}
    const q4Value: RiskAnswer = {risk: 'いいえ', depth: '', tp: '', tpValue: ''}
    try {
      answer.value.calcResult = ''

      const lon = answer.value.lng
      const lat = answer.value.lat

      // 標高
      let res = await axios.get(`https://suiboumap.gsi.go.jp/ShinsuiMap/api/search/SearchElevation?lon=${lon}&lat=${lat}`)
      let elevation = res.data && res.data.elevation ? res.data.elevation : null
      elevation = isNumeric(elevation) ? Number(elevation) : null
      if (elevation === null) {
        answer.value.calcResult = 'error-InvalidElevation'
        throw new Error('elevation is null')
      }

      // 想定最大規模浸水深
      res = await axios.get(`https://suiboumap.gsi.go.jp/shinsuimap/Api/Public/GetMaxDepth?lon=${lon}&lat=${lat}&CSVScale=0`)
      let saidai = res.data && res.data.Depth ? res.data.Depth : null
      saidai = getCeilDepth(saidai)

      // 計画規模浸水深
      res = await axios.get(`https://suiboumap.gsi.go.jp/shinsuimap/Api/Public/GetMaxDepth?lon=${lon}&lat=${lat}&CSVScale=1`)
      let keikaku = res.data && res.data.Depth ? res.data.Depth : null
      keikaku = getCeilDepth(keikaku)

      // 河岸浸食 and 内水
      res = await axios.get(`https://safdwszvfw4yfnaw26ljdpnsae0jucas.lambda-url.ap-northeast-1.on.aws/?lon=${lon}&lat=${lat}`)
      const apiRes = res.data
      const kagan = apiRes.kagan === true ? 'はい' : 'いいえ'

      // Q1
      if (saidai !== null) {
        q1Value.risk = saidai > 0 ? 'はい' : 'いいえ'
        q1Value.depth = saidai.toFixed(1)
        q1Value.tp = calcTp(saidai, elevation)
        q1Value.tpValue = calcTp(saidai, elevation)
      }

      // Q2
      if (keikaku !== null) {
        q2Value.risk = keikaku > 0 ? 'はい' : 'いいえ'
        q2Value.depth = keikaku.toFixed(1)
        q2Value.tp = calcTp(keikaku, elevation)
        q2Value.tpValue = calcTp(keikaku, elevation)
      }

      // Q3
      q3Value.risk = kagan

      // Q4
      // 内水氾濫
      let naisuiDepth: any = 0
      if (apiRes.naisui !== undefined && apiRes.naisui.depth !== undefined && apiRes.naisui.depth > 0) {
        naisuiDepth = getCeilDepth(apiRes.naisui.depth)
        const naisuiDem = apiRes.naisui.dem
        q4Value.risk = naisuiDepth > 0 ? 'はい' : 'いいえ'
        q4Value.depth = naisuiDepth.toFixed(1)
        q4Value.tp = calcTp(naisuiDepth, naisuiDem)
        q4Value.tpValue = calcTp(naisuiDepth, naisuiDem)
      }

      answer.value.calcResult = 'success'
      answer.value.saidai = String(saidai)
      answer.value.keikaku = String(keikaku)
      answer.value.naisui = String(naisuiDepth)
      answer.value.elevation = String(elevation)
    } catch (error) {
      if (answer.value.calcResult === '') {
        answer.value.calcResult = 'error'
      }
      answer.value.saidai = null
      answer.value.keikaku = null
      answer.value.naisui = null
      answer.value.elevation = null
      console.log(error)
    } finally {
      const valid = answer.value.calcResult === 'success'
      const nextAnswers = {
        ...state.app.answers,
        map: {value: answer.value, valid: true},
        [getQuestionKey(1, 1)]: {value: q1Value, valid},
        [getQuestionKey(2, 1)]: {value: q2Value, valid},
        [getQuestionKey(3, 1)]: {value: q3Value, valid},
        [getQuestionKey(4, 1)]: {value: q4Value, valid},
      }
      state.app.setAnswers(nextAnswers)
      setCalcResult(answer.value.calcResult)
      state.app.setLoading(false)
    }
  }

  return (
    <QuestionFrame q={1}>

      <Stack width={'100%'}>
        <Typography variant='h5' align='center' fontWeight={'bold'} color={blue[500]} whiteSpace={'pre-wrap'} sx={{textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)'}} >
          外水氾濫
        </Typography>
      </Stack>

      <Stack
        mb={2}
        direction="column"
        alignItems={'flex-end'}
        width={'100%'}
      >
        { calcResult === 'none' && <Typography variant='body1' color='error'>評価する物件位置を選択してください。</Typography> }
        { calcResult === 'none' && <Button variant='outlined' size='small' onClick={() => navigate('/map', {replace: false})}>物件位置選択</Button> }
        { calcResult === 'error' && <Typography variant='body1' color='error'>計算エラーが発生しました。</Typography> }
        { calcResult === 'error-InvalidElevation' && <Typography variant='body1' color='error'>標高の取得に失敗しました。陸地でない場合は正常に取得できません。</Typography> }
        { calcResult.startsWith('error') && <Button variant='outlined' size='small' onClick={calcRisk}>再計算する</Button> }
      </Stack>

      <Typography variant='h6' mb={1}>頑強性（立地）</Typography>

      <Stack direction={'row'} spacing={'4px'} mb={'4px'}>
        <BlueColumn width={160} text='' step={700} />
        <BlueColumn width={140} text={'氾濫リスクが\nあるか'} step={700} center />
        <BlueColumn width={140} text={'最大浸水深\n（ m ）'} step={700} center />
        <BlueColumn width={140} text={'最大浸水位\n（ T.P.m ）'} step={700} center />
      </Stack>
      <Stack direction={'row'} spacing={'4px'} mb={'4px'}>
        <BlueColumn width={160} text='1. 想定最大規模' step={700} />
        <BlueColumn width={140} text={q1.risk} step={50} center />
        <BlueColumn width={140} text={q1.depth === '' ? 'ー' : q1.depth} step={50} center />
        <BlueColumn width={140} text={q1.tp === '' ? 'ー' : q1.tp} step={50} center />
      </Stack>
      <Stack direction={'row'} spacing={'4px'} mb={'4px'}>
        <BlueColumn width={160} text='2. 年超過確率1/200' step={700} />
        <BlueColumn width={140} text={q2.risk} step={50} center />
        <BlueColumn width={140} text={q2.depth === '' ? 'ー' : q2.depth} step={50} center />
        <BlueColumn width={140} text={q2.tp === '' ? 'ー' : q2.tp} step={50} center />
      </Stack>
      <Stack direction={'row'} spacing={'4px'}>
        <BlueColumn width={304} text={'3.家屋倒壊等氾濫想定区域【河岸浸食】\nエリアに建物敷地があるか'} step={700} />
        <BlueColumn width={140} text={q3.risk} step={50} center />
      </Stack>

      <Stack width={'100%'} mt={'40px'}>
        <Typography variant='h5' align='center' fontWeight={'bold'} color={blue[500]} whiteSpace={'pre-wrap'} sx={{textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)'}} >
          内水氾濫
        </Typography>
      </Stack>

      <Typography variant='h6' mb={1}>頑強性（立地）</Typography>

      <Stack direction={'row'} spacing={'4px'} mb={'4px'}>
        <BlueColumn width={160} text='' step={700} />
        <BlueColumn width={140} text={'氾濫リスクが\nあるか'} step={700} center />
        <BlueColumn width={140} text={'最大浸水深\n（ m ）'} step={700} center />
        <BlueColumn width={140} text={'最大浸水位\n（ T.P.m ）'} step={700} center />
      </Stack>
      <Stack direction={'row'} spacing={'4px'} mb={'4px'}>
        <BlueColumn width={160} text='4. 想定最大規模' step={700} />
        <BlueColumn width={140} text={q4.risk} step={50} center />
        <BlueColumn width={140} text={q4.depth === '' ? 'ー' : q4.depth} step={50} center />
        <BlueColumn width={140} text={q4.tp === '' ? 'ー' : q4.tp} step={50} center />
      </Stack>

      <Typography variant='body2' mt={3} mb={1}>
        ・自己査定システムにおける最大浸水深については、概算による浸水深（0.5, 1.0, 2.0, 3.0, 4.0, 5.0m など）を評価に用います。
      </Typography>

    </QuestionFrame>
  )
}
